/**
 * SessionFileTray
 *
 * Reusable dropzone + file list for an upload session. Drops auto-upload to
 * the session's temp bucket; each file row expands to edit evidence metadata
 * (type, dates, issuer, etc.) before the file is promoted to permanent
 * storage by the parent flow.
 */

import { useCallback, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { FileDropZone } from '@features/files/FileDropZone';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { DebouncedTextInput } from '@g17eco/ui-modern/components/ui/debounced-text-input';
import { Popover, PopoverContent, PopoverTrigger } from '@g17eco/ui-modern/components/ui/popover';
import { bytesToHumanReadable } from '../../../utils';
import { toastFromError } from '@g17eco/molecules/toasts';
import {
  EvidenceType,
  type SessionFile,
  type SessionFileMetadataUpdate,
  type UploadSession,
} from '../types/emissions';
import { EVIDENCE_TYPE_LABEL, getMimeIcon } from '../utils/evidenceDisplay';

const EVIDENCE_MIME_TYPES = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
  'text/csv': ['.csv'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
};

const EVIDENCE_MAX_SIZE_BYTES = 25 * 1024 * 1024;

interface SessionFileTrayProps {
  session: UploadSession | null;
  /**
   * Upload callback. Accepts an optional `metadata` seed that is applied
   * server-side in the same POST — lets the tray pre-fill evidenceType
   * from a filename-keyword guess without a follow-up PATCH.
   */
  onUpload: (file: File, metadata?: SessionFileMetadataUpdate) => Promise<SessionFile>;
  onRemove: (fileId: string) => Promise<void>;
  onUpdateMetadata: (fileId: string, metadata: SessionFileMetadataUpdate) => Promise<SessionFile>;
  /** Controlled selection — which files are checked for attach. */
  selectedFileIds?: Set<string>;
  onSelectionChange?: (fileIds: Set<string>) => void;
  /** Hide the selection checkboxes (e.g., in drawer where we don't need bulk attach). */
  selectable?: boolean;
  /**
   * True while the parent's ensureSession() is in flight. The dropzone
   * disables so drops can't race a half-created session, and the prompt
   * swaps to "Preparing upload…" so the wait is visible.
   */
  isCreatingSession?: boolean;
  /**
   * Activities in scope for attachment. When `length > 1`, each row shows a
   * "Attach to" popover so users can route a specific file to a subset of
   * activities (e.g. meter-specific invoice for one of two activities on
   * the same asset). When `length <= 1` or undefined, the popover is hidden.
   */
  activityIds?: string[];
  /** Human-readable label per activity id for the popover. */
  activityLabels?: Record<string, string>;
  /**
   * Controlled per-file target-activity selection. Key = fileId,
   * value = subset of `activityIds` to link that file to. Defaults to
   * "all activityIds" for any file not present in the map.
   */
  targetActivitySelections?: Record<string, string[]>;
  onTargetActivitySelectionsChange?: (next: Record<string, string[]>) => void;
  /** Optional block rendered between the drop zone and the file list. */
  fileListHeader?: ReactNode;
}

export const SessionFileTray = ({
  session,
  onUpload,
  onRemove,
  onUpdateMetadata,
  selectedFileIds,
  onSelectionChange,
  selectable = true,
  isCreatingSession = false,
  activityIds,
  activityLabels,
  targetActivitySelections,
  onTargetActivitySelectionsChange,
  fileListHeader,
}: SessionFileTrayProps) => {
  const [uploadingNames, setUploadingNames] = useState<Set<string>>(new Set());
  const [expandedFileId, setExpandedFileId] = useState<string | null>(null);

  // Render every session file. Promoted ones are rendered as inert rows with
  // an "Attached" badge so the user sees the full history of files they've
  // staged in this session, not just the unpromoted tail. Earlier we filtered
  // them out entirely — that removed the disabled-with-no-reason confusion,
  // but also removed the context of "what else have I attached here".
  const files = session?.files ?? [];

  const handleDrop = useCallback(
    async (dropped: File[]) => {
      if (dropped.length === 0) {
        return;
      }
      // Seed the in-flight set with every dropped name up front so the
      // "Uploading N files…" counter reflects the real batch size instead
      // of ticking through 1/1 as each finishes.
      setUploadingNames((prev) => {
        const next = new Set(prev);
        for (const f of dropped) {
          next.add(f.name);
        }
        return next;
      });
      await Promise.all(
        dropped.map(async (file) => {
          try {
            // evidenceType is seeded server-side from the filename so every
            // client (and future non-browser uploaders) gets the same guess.
            await onUpload(file);
          } catch (error) {
            toastFromError({ error, title: `Failed to upload ${file.name}` });
          } finally {
            setUploadingNames((prev) => {
              const next = new Set(prev);
              next.delete(file.name);
              return next;
            });
          }
        }),
      );
    },
    [onUpload]
  );

  const toggleSelected = (fileId: string) => {
    if (!onSelectionChange || !selectedFileIds) {
      return;
    }
    const next = new Set(selectedFileIds);
    if (next.has(fileId)) {
      next.delete(fileId);
    } else {
      next.add(fileId);
    }
    onSelectionChange(next);
  };

  const handleMetadataChange = async (
    fileId: string,
    patch: SessionFileMetadataUpdate
  ) => {
    try {
      await onUpdateMetadata(fileId, patch);
    } catch (error) {
      toastFromError({ error, title: 'Failed to update metadata' });
    }
  };

  const handleRemove = async (fileId: string) => {
    try {
      await onRemove(fileId);
    } catch (error) {
      toastFromError({ error, title: 'Failed to remove file' });
    }
  };

  const showTargetPicker = (activityIds?.length ?? 0) > 1;
  const handleTargetActivityChange = (fileId: string, next: string[]) => {
    if (!onTargetActivitySelectionsChange) {
      return;
    }
    // Empty next = "all" (default). Delete the entry rather than storing an
    // explicit all-selection so the parent can tell default from narrowed.
    const updated = { ...targetActivitySelections };
    if (next.length === 0 || next.length === (activityIds?.length ?? 0)) {
      delete updated[fileId];
    } else {
      updated[fileId] = next;
    }
    onTargetActivitySelectionsChange(updated);
  };

  return (
    <div>
      <FileDropZone
        accept={EVIDENCE_MIME_TYPES}
        maxSize={EVIDENCE_MAX_SIZE_BYTES}
        multiple
        disabled={isCreatingSession}
        onDrop={handleDrop}
      >
        <div className='tw:flex tw:w-full tw:flex-col tw:items-center tw:gap-2 tw:p-6 tw:text-center tw:text-ThemeTextMedium'>
          {isCreatingSession ? (
            <>
              <i className='fa-regular fa-spinner-third fa-spin tw:text-2xl' aria-hidden />
              <div className='tw:text-sm tw:font-medium'>Preparing upload…</div>
            </>
          ) : (
            <>
              <i className='fa-light fa-cloud-arrow-up tw:text-2xl' aria-hidden />
              <div className='tw:text-sm tw:font-medium'>
                Drop evidence files here, or click to browse
              </div>
              <div className='tw:text-xs'>
                PDF, PNG, JPG, WebP, XLSX, CSV, DOCX · up to {EVIDENCE_MAX_SIZE_BYTES / (1024 * 1024)} MB each
              </div>
            </>
          )}
        </div>
      </FileDropZone>

      {fileListHeader ? <div className='tw:mt-3'>{fileListHeader}</div> : null}

      {uploadingNames.size + files.length > 0 ? (
        <ul className='tw:mt-4 tw:list-none tw:pl-0 tw:flex tw:flex-col tw:gap-2'>
          {[...uploadingNames].map((name) => (
            <PendingUploadRow key={`pending:${name}`} name={name} />
          ))}
          {files.map((file) => (
            <SessionFileRow
              key={file.fileId}
              file={file}
              expanded={expandedFileId === file.fileId}
              onToggleExpand={() =>
                setExpandedFileId(expandedFileId === file.fileId ? null : file.fileId)
              }
              onRemove={() => handleRemove(file.fileId)}
              onMetadataChange={(patch) => handleMetadataChange(file.fileId, patch)}
              selectable={selectable}
              selected={selectedFileIds?.has(file.fileId) ?? false}
              onToggleSelected={() => toggleSelected(file.fileId)}
              showTargetPicker={showTargetPicker}
              activityIds={activityIds ?? []}
              activityLabels={activityLabels ?? {}}
              targetSelection={targetActivitySelections?.[file.fileId] ?? activityIds ?? []}
              onTargetSelectionChange={(next) => handleTargetActivityChange(file.fileId, next)}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
};

const PendingUploadRow = ({ name }: { name: string }) => (
  <li className='tw:rounded tw:border tw:border-border/60 tw:p-3'>
    <div className='tw:flex tw:items-center tw:gap-3 tw:text-ThemeTextMedium'>
      <i className='fa-regular fa-spinner-third fa-spin' aria-hidden />
      <div className='tw:flex-1 tw:min-w-0'>
        <div className='tw:truncate tw:text-sm tw:font-medium'>{name}</div>
        <div className='tw:text-xs tw:text-ThemeTextLight'>Uploading…</div>
      </div>
    </div>
  </li>
);

interface SessionFileRowProps {
  file: SessionFile;
  expanded: boolean;
  onToggleExpand: () => void;
  onRemove: () => void;
  onMetadataChange: (patch: SessionFileMetadataUpdate) => void;
  selectable: boolean;
  selected: boolean;
  onToggleSelected: () => void;
  /** True when the parent flow has >1 activity and users may narrow the scope per file. */
  showTargetPicker: boolean;
  activityIds: string[];
  activityLabels: Record<string, string>;
  targetSelection: string[];
  onTargetSelectionChange: (next: string[]) => void;
}

const SessionFileRow = (props: SessionFileRowProps) => {
  // Dispatch by promotion state so the two row variants stay as separate,
  // clearly-named components. Promoted rows are inert metadata-wise but
  // still selectable for re-attach; staged rows carry the full editor.
  if (props.file.promotedToEvidenceId) {
    return <PromotedFileRow {...props} />;
  }
  return <StagedFileRow {...props} />;
};

type PromotedFileRowProps = Pick<
  SessionFileRowProps,
  | 'file'
  | 'selectable'
  | 'selected'
  | 'onToggleSelected'
  | 'showTargetPicker'
  | 'activityIds'
  | 'activityLabels'
  | 'targetSelection'
  | 'onTargetSelectionChange'
>;

/**
 * Inert-metadata row for a file that has already been promoted to an
 * EmissionEvidence doc. Still selectable so the user can re-attach the
 * same evidence to a new activity without re-uploading — the hook's
 * promoteSelected skips the promote step for these and just calls the
 * attach-only endpoint for each activity in scope.
 */
const PromotedFileRow = ({
  file,
  selectable,
  selected,
  onToggleSelected,
  showTargetPicker,
  activityIds,
  activityLabels,
  targetSelection,
  onTargetSelectionChange,
}: PromotedFileRowProps) => {
  return (
    <li className='tw:rounded tw:border tw:border-border/60 tw:bg-muted/30 tw:p-3'>
      <div className='tw:flex tw:items-center tw:gap-3'>
        {selectable ? (
          <input
            type='checkbox'
            checked={selected}
            onChange={onToggleSelected}
            aria-label={`Select ${file.originalName}`}
          />
        ) : null}
        <i className={`fa-regular ${getMimeIcon(file.mimeType)} tw:text-ThemeTextLight`} aria-hidden />
        <div className='tw:flex-1 tw:min-w-0'>
          <div className='tw:truncate tw:text-sm tw:font-medium'>{file.originalName}</div>
          <div className='tw:text-xs tw:text-ThemeTextLight'>
            {bytesToHumanReadable(file.fileSize)}
            {file.metadata.evidenceType
              ? ` · ${EVIDENCE_TYPE_LABEL[file.metadata.evidenceType]}`
              : ''}
          </div>
        </div>
        {showTargetPicker ? (
          <TargetActivityPicker
            activityIds={activityIds}
            activityLabels={activityLabels}
            selection={targetSelection}
            onChange={onTargetSelectionChange}
            aria-label={`Attach ${file.originalName} to activities`}
          />
        ) : null}
        <Badge variant='secondary'>Attached</Badge>
      </div>
    </li>
  );
};

/**
 * Fully-interactive row for a file still in the staging pipeline (not yet
 * promoted). Owns the checkbox, type select, per-row target picker, the
 * expandable metadata editor (issuer / description) and the remove button.
 */
const StagedFileRow = ({
  file,
  expanded,
  onToggleExpand,
  onRemove,
  onMetadataChange,
  selectable,
  selected,
  onToggleSelected,
  showTargetPicker,
  activityIds,
  activityLabels,
  targetSelection,
  onTargetSelectionChange,
}: SessionFileRowProps) => {
  return (
    <li className='tw:rounded tw:border tw:border-border/60 tw:p-3'>
      <div className='tw:flex tw:items-center tw:gap-3'>
        {selectable ? (
          <input
            type='checkbox'
            checked={selected}
            onChange={onToggleSelected}
            aria-label={`Select ${file.originalName}`}
          />
        ) : null}
        <i className={`fa-regular ${getMimeIcon(file.mimeType)} tw:text-ThemeTextMedium`} aria-hidden />
        <div className='tw:flex-1 tw:min-w-0'>
          <div className='tw:truncate tw:text-sm tw:font-medium'>{file.originalName}</div>
          <div className='tw:text-xs tw:text-ThemeTextLight'>
            {bytesToHumanReadable(file.fileSize)}
          </div>
        </div>
        {/* Inline type select — the canonical action on a row. Expanded view
          * carries the less-common optional fields (issuer / description). */}
        <select
          className={`tw:rounded tw:border tw:border-border tw:p-1 tw:text-xs ${
            file.metadata.evidenceType ? '' : 'tw:border-ThemeAccentDanger tw:text-ThemeAccentDanger'
          }`}
          value={file.metadata.evidenceType ?? ''}
          aria-label={`Type for ${file.originalName}`}
          onChange={(e) =>
            onMetadataChange({
              evidenceType: (e.target.value || undefined) as EvidenceType | undefined,
            })
          }
        >
          <option value=''>— Select type —</option>
          {Object.entries(EVIDENCE_TYPE_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        {showTargetPicker ? (
          <TargetActivityPicker
            activityIds={activityIds}
            activityLabels={activityLabels}
            selection={targetSelection}
            onChange={onTargetSelectionChange}
            aria-label={`Attach ${file.originalName} to activities`}
          />
        ) : null}
        <Button variant='link' size='sm' onClick={onToggleExpand}>
          {expanded ? 'Done' : 'More'}
          {expanded ? (
            <ChevronUp className='tw:size-3 tw:ml-1' aria-hidden />
          ) : (
            <ChevronDown className='tw:size-3 tw:ml-1' aria-hidden />
          )}
        </Button>
        <Button
          variant='ghost'
          size='icon-sm'
          onClick={onRemove}
          aria-label={`Remove ${file.originalName}`}
        >
          <i className='fa fa-times' />
        </Button>
      </div>

      {expanded ? (
        <div className='tw:mt-3 tw:grid tw:grid-cols-1 tw:gap-3'>
          {/* Activity carries the authoritative period and doc dates were
            * rarely populated — per-file extras stay minimal: issuer for
            * provenance, description for free-text notes. */}
          <DebouncedTextInput
            label='Issuer / supplier'
            value={file.metadata.issuer ?? ''}
            onCommit={(next) => onMetadataChange({ issuer: next })}
          />
          <DebouncedTextInput
            label='Description'
            value={file.metadata.description ?? ''}
            multiline
            onCommit={(next) => onMetadataChange({ description: next })}
          />
        </div>
      ) : null}
    </li>
  );
};

interface TargetActivityPickerProps {
  activityIds: string[];
  activityLabels: Record<string, string>;
  selection: string[];
  onChange: (next: string[]) => void;
  'aria-label': string;
}

const TargetActivityPicker = ({
  activityIds,
  activityLabels,
  selection,
  onChange,
  'aria-label': ariaLabel,
}: TargetActivityPickerProps) => {
  const isAll = selection.length === activityIds.length;
  const label = isAll
    ? `All (${activityIds.length})`
    : `${selection.length} of ${activityIds.length}`;

  const toggle = (id: string) => {
    if (selection.includes(id)) {
      onChange(selection.filter((s) => s !== id));
      return;
    }
    onChange([...selection, id]);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant='outline' size='sm' aria-haspopup='listbox' aria-label={ariaLabel}>
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent role='listbox' align='end' className='tw:w-56 tw:p-2'>
        <div className='tw:mb-1 tw:text-xs tw:text-ThemeTextMedium'>
          Attach to activities
        </div>
        {activityIds.map((id) => (
          <label
            key={id}
            className='tw:flex tw:items-center tw:gap-2 tw:py-1 tw:text-sm'
          >
            <input
              type='checkbox'
              checked={selection.includes(id)}
              onChange={() => toggle(id)}
            />
            <span className='tw:truncate'>{activityLabels[id] ?? id.slice(-6)}</span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
};
